<template>
  <div class="settings">
    <Sidebar />
    <div class="content-wrapper">
      <div class="header">
        <div class="title-section">
          <h1>Settings</h1>
          <p class="subtitle">Configure your application settings and preferences</p>
        </div>
        <div v-if="error" class="error-message">
          <i class="fas fa-exclamation-circle"></i>
          {{ error }}
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading settings...</p>
      </div>

      <div v-else class="settings-container">
        <!-- General Settings -->
        <div class="settings-card">
          <h3><i class="fas fa-cog"></i> General Settings</h3>
          <div class="settings-grid">
            <div class="form-group">
              <label>App Name</label>
              <input v-model="settings.general.app_name" type="text" class="input-field">
            </div>
            <div class="form-group">
              <label>App Description</label>
              <QuillEditor
                v-model:content="settings.general.app_description"
                label="App Description"
                :toolbar="toolbarOptions"
                placeholder="Enter app description"
              />
            </div>
            <div class="form-group">
              <label>Contact Email</label>
              <input v-model="settings.general.contact_email" type="email" class="input-field">
            </div>
            <div class="form-group">
              <label>Support Phone</label>
              <input v-model="settings.general.support_phone" type="tel" class="input-field">
            </div>
            <div class="form-group">
              <label>Timezone</label>
              <select v-model="settings.general.timezone" class="input-field">
                <option value="UTC">UTC</option>
                <option value="America/New_York">America/New_York</option>
                <option value="Europe/London">Europe/London</option>
              </select>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="settings.general.maintenance_mode">
                <span>Enable Maintenance Mode</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Email Settings -->
        <div class="settings-card">
          <h3><i class="fas fa-envelope"></i> Email Settings</h3>
          <div class="settings-grid">
            <div class="form-group">
              <label>Mail Driver</label>
              <select v-model="settings.email.mail_driver" class="input-field">
                <option value="smtp">SMTP</option>
                <option value="sendmail">Sendmail</option>
                <option value="mailgun">Mailgun</option>
              </select>
            </div>
            <div class="form-group">
              <label>Mail Host</label>
              <input v-model="settings.email.mail_host" type="text" class="input-field">
            </div>
            <div class="form-group">
              <label>Mail Port</label>
              <input v-model="settings.email.mail_port" type="number" class="input-field">
            </div>
            <div class="form-group">
              <label>Mail Username</label>
              <input v-model="settings.email.mail_username" type="text" class="input-field">
            </div>
            <div class="form-group">
              <label>Mail Password</label>
              <input v-model="settings.email.mail_password" type="password" class="input-field">
            </div>
            <div class="form-group">
              <label>Mail From Address</label>
              <input v-model="settings.email.mail_from_address" type="email" class="input-field">
            </div>
          </div>
        </div>

        <!-- Payment Settings -->
        <div class="settings-card">
          <h3><i class="fas fa-credit-card"></i> Payment Settings</h3>
          <div class="settings-grid">
            <div class="form-group">
              <label>Stripe Key</label>
              <input v-model="settings.payment.stripe_key" type="text" class="input-field">
            </div>
            <div class="form-group">
              <label>Stripe Secret</label>
              <input v-model="settings.payment.stripe_secret" type="password" class="input-field">
            </div>
            <div class="form-group">
              <label>PayPal Client ID</label>
              <input v-model="settings.payment.paypal_client_id" type="text" class="input-field">
            </div>
            <div class="form-group">
              <label>PayPal Secret</label>
              <input v-model="settings.payment.paypal_secret" type="password" class="input-field">
            </div>
            <div class="form-group">
              <label>Currency</label>
              <select v-model="settings.payment.currency" class="input-field">
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Security Settings -->
        <div class="settings-card">
          <h3><i class="fas fa-shield-alt"></i> Security Settings</h3>
          <div class="settings-grid">
            <div class="form-group">
              <label>Minimum Password Length</label>
              <input v-model="settings.security.password_min_length" type="number" min="8" class="input-field">
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="settings.security.password_requires_special">
                <span>Require Special Characters</span>
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="settings.security.password_requires_number">
                <span>Require Numbers</span>
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="settings.security.two_factor_enabled">
                <span>Enable Two-Factor Authentication</span>
              </label>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <button @click="resetSettings" class="btn btn-secondary">
            <i class="fas fa-undo"></i> Reset
          </button>
          <button @click="saveSettings" class="btn btn-primary">
            <i class="fas fa-save"></i> Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'SettingsView',
  components: { 
    Sidebar,
    QuillEditor
  },

  setup() {
    const loading = ref(true);
    const settings = ref({
      general: {},
      email: {},
      payment: {},
      security: {},
      notification: {},
      api: {}
    });
    const originalSettings = ref(null);
    const toolbarOptions = ref([
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ]);

    const fetchSettings = async () => {
      try {
        const response = await axios.get('https://api.kewaar.com/api/admin/settings', {
          headers: {
            'Authorization': 'Bearer 4772|9c59cECmt9uUr6o2UpP27l2VXjAP8tCdwpKoUYZ52e3047e5'
          }
        });
        
        if (response.data.success) {
          settings.value = response.data.data;
          originalSettings.value = JSON.parse(JSON.stringify(response.data.data));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        loading.value = false;
      }
    };

    const saveSettings = async () => {
      try {
        const response = await axios.post('https://api.kewaar.com/api/admin/settings', settings.value, {
          headers: {
            'Authorization': 'Bearer 4772|9c59cECmt9uUr6o2UpP27l2VXjAP8tCdwpKoUYZ52e3047e5'
          }
        });
        
        if (response.data.success) {
          alert('Settings saved successfully');
          originalSettings.value = JSON.parse(JSON.stringify(settings.value));
        }
      } catch (error) {
        console.error('Error saving settings:', error);
        alert('Error saving settings');
      }
    };

    const resetSettings = () => {
      if (confirm('Are you sure you want to reset all changes?')) {
        settings.value = JSON.parse(JSON.stringify(originalSettings.value));
      }
    };

    onMounted(() => {
      fetchSettings();
    });

    return {
      loading,
      settings,
      saveSettings,
      resetSettings,
      toolbarOptions
    };
  }
};
</script>

<style scoped>
.settings {
  min-height: 100vh;
  background-color: #f8fafc;
}

.content-wrapper {
  padding: 2rem;
  margin-left: 320px;
}

.header {
  margin-bottom: 2.5rem;
}

.title-section {
  margin-bottom: 1rem;
}

.title-section h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
}

.subtitle {
  margin: 0.5rem 0 0;
  color: #64748b;
  font-size: 1rem;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc2626;
  padding: 1rem;
  background: #fef2f2;
  border-left: 4px solid #dc2626;
  border-radius: 6px;
  margin-top: 1rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.settings-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: fadeIn 0.3s ease;
}

.settings-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.settings-card:hover {
  transform: translateY(-2px);
}

.settings-card h3 {
  margin: 0 0 1.5rem;
  color: #1e293b;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.settings-card h3 i {
  color: #2563eb;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
}

.input-field {
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #1e293b;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.input-field:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

.btn-primary {
  background: #2563eb;
  color: white;
  border: none;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f8fafc;
  color: #1e293b;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .content-wrapper {
    margin-left: 0;
    padding: 1rem;
  }
  
  .settings-grid {
    grid-template-columns: 1fr;
  }
}
</style> 